import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadOverlappingLeaves } from "../../redux/rtk/features/leave/leaveSlice";

const LeaveCalendar = () => {
    const dispatch = useDispatch();
    const overlap = useSelector((state) => state.leave.overlappingLeaves);
    const [calendarData, setCalendarData] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [leaveTypeColors, setLeaveTypeColors] = useState({});

    useEffect(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        // console.log("firstDay", firstDay);
        // console.log("lastDay", lastDay);

        // Format dates as local YYYY-MM-DD (no UTC conversion)
        const leaveFrom = dayjs(firstDay).format('YYYY-MM-DD');
        const leaveTo = dayjs(lastDay).format('YYYY-MM-DD');

        // console.log("firstDayS", leaveFrom);
        // console.log("lastDayS", leaveTo);

        setDateRange({ from: leaveFrom, to: leaveTo });

        // Dispatch action to load leaves for this date range
        dispatch(loadOverlappingLeaves({ leaveFrom, leaveTo }));
    }, [currentMonth, dispatch]);

    // Generate calendar data when the month or overlap data changes
    useEffect(() => {
        if (overlap && overlap.length > 0) {
            const uniqueLeaveTypes = new Set();

            // Collect all unique leave types
            overlap.forEach(leave => {
                if (leave.leaveType) {
                    uniqueLeaveTypes.add(leave.leaveType);
                }
            });

            const colorClasses = [
                'bg-blue-100 border-blue-300',
                'bg-yellow-100 border-yellow-300',
                'bg-green-100 border-green-300',
                'bg-pink-100 border-pink-300',
                'bg-teal-100 border-teal-300',
                'bg-indigo-100 border-indigo-300',
                'bg-cyan-100 border-cyan-300',
                'bg-purple-100 border-purple-300',
                'bg-orange-100 border-orange-300',
                'bg-red-100 border-red-300',

            ];

            // Assign a color to each leave type
            const newLeaveTypeColors = {};
            Array.from(uniqueLeaveTypes).forEach((leaveType, index) => {

                newLeaveTypeColors[leaveType] = colorClasses[index % colorClasses.length];
            });

            setLeaveTypeColors(newLeaveTypeColors);
        }
    }, [overlap]);


    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            generateCalendarData(currentMonth);
        }
    }, [currentMonth, overlap, dateRange]);

    const generateCalendarData = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();

        // Get first day of month and last day of month
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Get the day of week the first day falls on (0 = Sunday, 1 = Monday, etc.)
        const firstDayOfWeek = firstDay.getDay();

        // Adjust for starting on Monday (0 = Monday in our UI)
        const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

        // Create calendar grid
        const daysInMonth = lastDay.getDate();
        const totalDays = Math.ceil((daysInMonth + startOffset) / 7) * 7;

        const days = [];
        let dayCounter = 1 - startOffset;

        for (let i = 0; i < totalDays; i++) {
            const currentDate = new Date(year, month, dayCounter);
            const isCurrentMonth = currentDate.getMonth() === month;

            // Filter leaves for this date
            const leavesForDay = filterLeavesForDate(currentDate);

            days.push({
                date: currentDate,
                dayOfMonth: currentDate.getDate(),
                isCurrentMonth,
                leaves: leavesForDay
            });

            dayCounter++;
        }

        // Group days into weeks
        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }

        setCalendarData(weeks);
    };

    const filterLeavesForDate = (date) => {
        if (!overlap) return [];

        return overlap.filter(leaveItem => {
            const fromDate = new Date(leaveItem.leaveFrom);
            const toDate = new Date(leaveItem.leaveTo);

            // Reset time parts to compare just the dates
            const compareDate = new Date(date);
            compareDate.setHours(0, 0, 0, 0);
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(0, 0, 0, 0);

            return compareDate >= fromDate && compareDate <= toDate;
        });
    };

    const navigateMonth = (step) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + step);
        setCurrentMonth(newMonth);
    };

    const formatMonth = (date) => {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const getLeaveTypeColor = (leaveType) => {
        return leaveTypeColors[leaveType] || 'bg-gray-100 border-gray-300'; // Default color
    };


    // Collect all unique leave types for the legend
    const getUniqueLeaveTypes = () => {
        return Object.keys(leaveTypeColors);
    };

    const uniqueLeaveTypes = getUniqueLeaveTypes();

    return (
        <div className="w-full p-1">
            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={() => navigateMonth(-1)}
                    className="text-blue-500 hover:text-blue-700"
                >
                    &lt; Previous month
                </button>
                <h2 className="text-xl font-semibold">{formatMonth(currentMonth)}</h2>
                <button
                    onClick={() => navigateMonth(1)}
                    className="text-blue-500 hover:text-blue-700"
                >
                    Next month &gt;
                </button>
            </div>

            {/* Calendar Status */}
            {/* <div className="mb-4 text-sm text-gray-600">
                Showing leaves from: {new Date(dateRange.from).toLocaleDateString()} to {new Date(dateRange.to).toLocaleDateString()}
            </div> */}

            <div className="grid grid-cols-7 gap-px bg-blue-200">
                <div className="bg-white p-2 text-center font-semibold">Mon</div>
                <div className="bg-white p-2 text-center font-semibold">Tue</div>
                <div className="bg-white p-2 text-center font-semibold">Wed</div>
                <div className="bg-white p-2 text-center font-semibold">Thu</div>
                <div className="bg-white p-2 text-center font-semibold">Fri</div>
                <div className="bg-white p-2 text-center font-semibold">Sat</div>
                <div className="bg-white p-2 text-center font-semibold">Sun</div>

                {calendarData.map((week, weekIndex) => (
                    <React.Fragment key={`week-${weekIndex}`}>
                        {week.map((day, dayIndex) => (
                            <div
                                key={`day-${weekIndex}-${dayIndex}`}
                                className={`bg-white border p-1 min-h-32 ${!day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}`}

                            >
                                <div className="text-right text-xs mb-1">
                                    {day.dayOfMonth} {day.isCurrentMonth && day.date.getDate() === 1 ?
                                        day.date.toLocaleString('default', { month: 'short' }) : ''}
                                </div>
                                {/*<div className="space-y-1">
                                    {day.leaves.map((leaveItem, idx) => (
                                        <div
                                            key={`leave-${leaveItem.leaveId}-${idx}`}
                                            className={`text-xs p-1 border rounded ${getLeaveTypeColor(leaveItem.leaveType)} truncate`}
                                            title={`${leaveItem.userName} - ${leaveItem.leaveType}`}
                                        >
                                            {leaveItem.userName}
                                        </div>
                                    ))}
                                </div> */}
                                <div className="space-y-1">
                                    {[...day.leaves]
                                        .sort((a, b) => a.userName.localeCompare(b.userName))
                                        .map((leaveItem, idx) => (
                                            <div
                                                key={`leave-${leaveItem.leaveId}-${idx}`}
                                                className={`text-xs p-1 border rounded ${getLeaveTypeColor(leaveItem.leaveType)} truncate`}
                                                title={`${leaveItem.userName} - ${leaveItem.leaveType}`}
                                            >
                                                {leaveItem.userName}
                                            </div>
                                        ))}
                                </div>

                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>

            {/* Legend */}
            <div className="mt-4">
                <div className="text-sm font-medium mb-2">Leave Types:</div>
                <div className="flex flex-wrap gap-3">
                    {uniqueLeaveTypes.map(leaveType => (
                        <div key={leaveType} className="flex items-center text-xs">
                            <div className={`w-3 h-3 mr-1 rounded ${getLeaveTypeColor(leaveType).split(' ')[0]}`}></div>
                            <span>{leaveType}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const LeaveContradiction = () => {
    return <LeaveCalendar />;
};

export default LeaveContradiction;